import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { gradeAnswer } from '@/lib/api/interview';
import { fetchRandomOnboardingQuestion } from '@/lib/api/questions';
import { track } from '@/lib/analytics';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnboardingShell from '@/components/layout/OnboardingShell';
import VoiceInput from '@/components/interview/VoiceInput';
import { getOnboardingGradingError } from '@/lib/errors/onboardingGradingErrors';
import { logger } from '@/lib/logger';
import { captureFriendlyError } from '@/lib/sentry';
import type { Question } from '@/types/database';
import type { FriendlyError } from '@/lib/errors/types';

export default function QuickWinPage() {
  const navigate = useNavigate();
  const { setQuickWinResult, setStep } = useOnboardingContext();

  const [question, setQuestion] = useState<Question | null>(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(true);
  const [questionError, setQuestionError] = useState<FriendlyError | null>(null);

  const [mode, setMode] = useState<'voice' | 'text'>('voice');
  const [typedAnswer, setTypedAnswer] = useState('');
  const [submittedAnswer, setSubmittedAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctAnswerText, setCorrectAnswerText] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<FriendlyError | null>(null);
  const [isGrading, setIsGrading] = useState(false);
  const [gradingError, setGradingError] = useState<FriendlyError | null>(null);

  const loadQuestion = useCallback(async () => {
    setIsLoadingQuestion(true);
    setQuestionError(null);
    const result = await fetchRandomOnboardingQuestion();
    if (result.success && result.question) {
      setQuestion(result.question);
    } else if (result.error) {
      setQuestionError(result.error);
    }
    setIsLoadingQuestion(false);
  }, []);

  useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  const grade = async (text: string) => {
    if (!question) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    setGradingError(null);
    setIsGrading(true);
    const result = await gradeAnswer(question.id, trimmed);
    setIsGrading(false);

    if (!result.success || result.isCorrect === undefined) {
      const code = result.error?.code ?? 'grading_failed';
      const friendly = getOnboardingGradingError(code);
      const context = { questionId: question.id, apiCode: code };
      logger.error('Onboarding grading failed', result.error?.raw, context);
      captureFriendlyError(result.error?.raw, friendly, context);
      setGradingError(friendly);
      return;
    }

    const correct = result.isCorrect;
    const correctAnswer =
      result.correctAnswer || question.answer_config?.accepted_answers?.[0] || null;

    setSubmittedAnswer(trimmed);
    setIsCorrect(correct);
    setCorrectAnswerText(correctAnswer);
    setSubmitted(true);
    setQuickWinResult({ correct, transcript: trimmed });
  };

  const handleVoiceTranscript = (text: string) => {
    setVoiceError(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setVoiceError({
        code: 'no_speech',
        title: "We didn't catch that",
        message: "We couldn't hear any speech in the recording.",
        action: 'Try again, or type your answer instead.',
        retryable: true,
        category: 'validation',
      });
      return;
    }
    grade(trimmed);
  };

  const handleVoiceError = (err: FriendlyError) => {
    setVoiceError(err);
    if (err.code === 'permission_denied') setMode('text');
  };

  const handleTypedSubmit = () => grade(typedAnswer);

  const handleContinue = () => {
    setStep(7);
    track('onboarding_step_completed', { step: 'quick_win' });
    navigate('/onboarding/soft-paywall');
  };

  const questionKeywords = question
    ? Array.from(
        new Set(
          (question.answer_config?.accepted_answers ?? []).flatMap((a) =>
            a.split(/\s+/).filter((word) => word.length > 3),
          ),
        ),
      )
    : [];

  return (
    <OnboardingShell
      step={6}
      title={<>Let&apos;s try a practice question!</>}
      subtitle={<>Just like the real interview — say it out loud.</>}
    >
      <div className="rounded-2xl border-2 border-border bg-card p-6 md:p-8 mb-6 min-h-[110px] flex items-center">
        {isLoadingQuestion ? (
          <div className="w-full flex items-center justify-center py-2">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : questionError ? (
          <div className="w-full space-y-3">
            <ErrorAlert error={questionError} />
            <Button fullWidth onClick={loadQuestion}>
              Try again
            </Button>
          </div>
        ) : question ? (
          <div className="w-full">
            <p className="font-sans text-sm text-text-secondary uppercase tracking-wider mb-3">
              Sample Question
            </p>
            <p className="font-display font-semibold text-xl md:text-2xl text-text-primary leading-snug">
              {question.question_text}
            </p>
          </div>
        ) : null}
      </div>

      {!submitted && !isLoadingQuestion && !questionError && question && mode === 'voice' && (
        <div className="space-y-5">
          <div className="py-4">
            <VoiceInput
              onTranscript={handleVoiceTranscript}
              onError={handleVoiceError}
              questionKeywords={questionKeywords}
              disabled={isGrading}
            />
          </div>

          {isGrading && (
            <div className="flex items-center justify-center gap-2 text-text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="font-sans text-sm">Grading your answer…</span>
            </div>
          )}

          {voiceError && <ErrorAlert error={voiceError} />}
          {gradingError && <ErrorAlert error={gradingError} />}

          <div className="text-center">
            <button
              onClick={() => {
                setMode('text');
                setVoiceError(null);
                setGradingError(null);
              }}
              className="font-sans text-sm text-text-secondary underline underline-offset-2 hover:text-primary cursor-pointer"
            >
              Type instead
            </button>
          </div>
        </div>
      )}

      {!submitted && !isLoadingQuestion && !questionError && question && mode === 'text' && (
        <div className="space-y-4">
          <input
            type="text"
            value={typedAnswer}
            onChange={(e) => setTypedAnswer(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !isGrading && handleTypedSubmit()}
            placeholder="Type your answer..."
            disabled={isGrading}
            className="w-full px-4 py-3 rounded-xl bg-input-bg border-2 border-border text-base font-sans text-text-primary outline-none focus:border-primary transition-colors disabled:opacity-60"
            autoFocus
          />
          <Button
            fullWidth
            onClick={handleTypedSubmit}
            disabled={!typedAnswer.trim() || isGrading}
          >
            {isGrading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Grading…
              </span>
            ) : (
              'Submit'
            )}
          </Button>

          {gradingError && <ErrorAlert error={gradingError} />}

          <div className="text-center pt-2">
            <button
              onClick={() => {
                setMode('voice');
                setGradingError(null);
              }}
              className="font-sans text-sm text-text-secondary underline underline-offset-2 hover:text-primary cursor-pointer"
            >
              Use the mic instead
            </button>
          </div>
        </div>
      )}

      {submitted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4"
        >
          <div className="rounded-xl border-2 border-border bg-card p-4">
            <p className="font-sans text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
              You said
            </p>
            <p className="font-display text-lg leading-snug text-text-primary">
              &ldquo;{submittedAnswer}&rdquo;
            </p>
          </div>

          <div
            className={`rounded-2xl border-2 p-6 ${
              isCorrect ? 'border-success bg-success/10' : 'border-error bg-error/10'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{isCorrect ? '🎉' : '💪'}</span>
              <p className="font-display font-semibold text-lg text-text-primary">
                {isCorrect ? 'Correct!' : 'Not quite, but great try!'}
              </p>
            </div>
            {!isCorrect && correctAnswerText && (
              <p className="font-sans text-sm text-text-secondary mb-2">
                The correct answer is &ldquo;{correctAnswerText}.&rdquo;
              </p>
            )}
            {question?.explanation && (
              <p className="font-sans text-sm text-text-secondary">
                {question.explanation}
              </p>
            )}
          </div>

          <Button fullWidth onClick={handleContinue}>
            Continue
          </Button>
        </motion.div>
      )}
    </OnboardingShell>
  );
}
